<script setup>
    import { useSitesStore } from '@/stores/sites';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useEventsStore } from '@/stores/events';
    import { useToursStore } from '@/stores/tours';
    import { updateHead } from '@/composables/helpers/updateHead';
    import { storeToRefs } from 'pinia';
    import { useMediaQuery } from '@vueuse/core'
    import { useFooterCta } from '@/composables/useFooterCta';
    import { detailTitle, seoDescription, shareImage } from '@/utils/seo';

    // Renders the tours CTA as part of the global footer block rather than in
    // this page, so the CTA and footer are one unit pinned to the window bottom.
    useFooterCta();

    const route = useRoute();

    const mapView = ref(false);
    const showCredit = ref(false);

    // Props
    const props = defineProps({
        pathId: {
            required: true,
            type: String,
            default: '',
        },
    });

    const { sitesData } = storeToRefs(useSitesStore());
    const siteData = computed(() => sitesData.value.find((site) => site.id == props.pathId));

    // A real 404 for an id that isn't a site, instead of a 200 with an empty
    // page that search engines index as a soft 404. Only once the sites have
    // actually loaded: if the bootstrap fetch failed the list is empty, and
    // 404ing every site page for that would be far worse than the soft 404.
    if (import.meta.server && sitesData.value?.length && !siteData.value) {
        throw createError({ statusCode: 404, statusMessage: 'Site not found', fatal: true });
    }

    const mapsData = computed(() => {
        let arr = [];
        if (siteData) arr.push(siteData);
        return arr;
    });

    const { neighborhoodsData, neighborhoodsMap } = storeToRefs(useNeighborhoodsStore());
    const neighborhoods = neighborhoodsData.value;

    const siteNeighborhood = computed(() => {
        const neighborhoodID = siteData?.value?.fields['OHC Neighborhood']?.[0];
        return neighborhoodID && neighborhoodsMap.value ? neighborhoodsMap.value[neighborhoodID] : false;
    });

    const { toursData } = storeToRefs(useToursStore());
    const tours = computed(() => toursData.value);

    const ohcTours = computed(() => {
        // `Organizations` lists every site on the itinerary. This compared against
        // [0] only, so the block appeared solely on whichever site happened to be
        // first — which is why it looked intermittent (Asana O2-42). The site that
        // was reported missing sits at index 6 of one itinerary and index 2 of
        // another; the one it was compared against sits at index 0.
        return tours.value?.records?.filter((el) => {
            const organizations = el?.fields?.['Organizations'];
            return Array.isArray(organizations) && organizations.includes(props.pathId);
        });
    });

    const eventsStore = useEventsStore();
    const siteEvents = computed(() => {
        const eventsData = eventsStore.eventsData;
        if (!eventsData || !eventsData.records) {
            return [];
        }
        return eventsData.records.filter((el) => {
            if (el?.fields?.Site) {
                return el?.fields?.Site[0] === siteData?.value?.id;
            }
            return false;
        });
    });

    const credits = computed(() => {
        const creditArr = [];
        if (siteData?.value?.fields?.['Image Credit']) {
            const photos = siteData?.value?.fields['Selected Photos'];
            photos.forEach((photo) => {
                creditArr.push(siteData?.value?.fields['Image Credit']);
            });
        }
        return creditArr.reverse();
    });

    // Airtable's multi-select. "Other (please specify below)" is a prompt to
    // the site, not something to show a visitor: the explanation carries it.
    const entranceRestrictions = computed(() => (siteData?.value?.fields?.['Entrance Restrictions'] || []).filter((value) => !value.startsWith('Other')));
    const hasEntranceRestrictions = computed(() => entranceRestrictions.value.length > 0 || !!siteData?.value?.fields?.['Entrance Restriction Explanation']);

    const desc = computed(() => {
        return siteData?.value?.fields?.['Web Description'] || '';
    });

    const photos = computed(() => {
        const arr = siteData?.value.fields?.['Selected Photos'];
        return arr ? arr.slice().reverse() : false;
    });

    const isMobile = useMediaQuery('(max-width: 767px)')

    onMounted(() => {
        if (siteData?.value) {
            const rawSource = route.query.source ?? history.state?.source;
            const source = (['list', 'map', 'search'].includes(rawSource) ? rawSource : 'list');
            const { track } = useAnalytics();
            track('site_viewed', {
                site_id: siteData.value.id,
                site_name: siteData.value.fields?.['Site'] || '',
                neighborhood: siteNeighborhood?.value?.fields?.Name || '',
                source,
            });
        }
    });

    // `desc` was passed here as the computed itself rather than its value, so
    // site pages shipped no meta description at all. Now plain text, cut at a
    // word boundary, from the same Web Description the page shows. The share
    // image is the site's lead photo rather than the generic card.
    updateHead(() => {
        const name = siteData.value?.fields?.['Site'];
        const image = shareImage(siteData.value?.fields?.['Selected Photos'], name);
        return {
            title: detailTitle(name),
            description: seoDescription(desc.value),
            path: route.path,
            image: image.url,
            imageWidth: image.width,
            imageHeight: image.height,
            imageAlt: image.alt,
        };
    });
</script>

<template>
    <section>
        <article class="site-detail" v-if="siteData">

            <button class="map-toggle top-actions" @click="mapView = !mapView" :class="{ active: mapView }" :aria-pressed="mapView" :aria-label="mapView ? 'Show Photos' : 'Show Map'">
                <SvgsMapWhiteCircle v-show="!mapView" />
                <SvgsCamera v-show="mapView" />
            </button>

            <BlocksMap class="map" :locations="mapsData" v-if="mapView" mapOnly singlePoint heightClass="short" zoom-control
                show-user-location zoom-default zoom-control-position="RIGHT_BOTTOM" />            

            <div v-show="!mapView" class="image-wrapper">
                <BlocksImageryHero v-if="photos" :photos="photos" :credits="credits" />
            </div>

            <div v-if="showCredit && !mapView" class="credit">
                <p v-html="siteData.fields['Image Credit']"></p>
            </div>

            <div class="hd-container">

            <!-- Gated on the SITE, not its neighborhood. This was
                 `v-if="siteNeighborhood && siteNeighborhood?.fields"`, which hid
                 the site's name, address and type whenever its OHC Neighborhood
                 wasn't in the synced neighborhoods collection — 50 of 202
                 confirmed sites, whose detail pages had no heading at all. The
                 neighborhood tag below has always had its own v-if. -->
            <section v-if="siteData?.fields" class="header-details container">
                <!-- Above the neighborhood tag, as the comp places it. -->
                <GlobalSiteLabel class="detail-site-label" :site="siteData" />
                <p v-if="siteNeighborhood?.fields?.Name" class="gray-tag">
                    {{ siteNeighborhood.fields.Name }}
                </p>
                <h1 class="headline-1">{{ siteData.fields['Site'] }}</h1>
                <p class="body-book address">{{ siteData.fields['Site Address'] }}</p>
                <p v-if="siteData.fields['Site Type']" class="site-type">
                    {{ siteTypeLabel(siteData.fields['Site Type']) }}
                </p>
            </section>

            <div class="outer-container">
                <GlobalActions class="actions" :item="siteData" useItinerary useDirectionsSingle :useDirections="false" useShare useFavorite />
            </div>

            <BlocksSiteHours class="hours" :site="siteData" />

            <section class="mobile-instructions" v-if="isMobile && siteData?.fields['Special entrance Instructions']">
                <h2 class="headline-3">Entrance Instructions</h2>
                <BlocksDetailDescription>
                    <p v-html="siteData.fields['Special entrance Instructions']"></p>
                </BlocksDetailDescription>
            </section>

            <section class="mobile-instructions" v-if="isMobile && hasEntranceRestrictions">
                <h2 class="headline-3">Entrance Restrictions</h2>
                <BlocksDetailDescription>
                    <p v-for="restriction in entranceRestrictions" :key="restriction">{{ restriction }}</p>
                    <p v-if="siteData?.fields['Entrance Restriction Explanation']" v-html="siteData.fields['Entrance Restriction Explanation']"></p>
                </BlocksDetailDescription>
            </section>

            <div class="amenities-site-meta-wrapper">
                <BlocksAmenities :site="siteData" />

                <div class="site-meta-container">
                    <BlocksSiteMeta class="meta" :site="siteData" />
                </div>
            </div>

            <section class="building-details" v-if="siteData?.fields['Web Description']">
                <h2 class="details-title container">Details</h2>
                <BlocksDetailDescription class="building-description">
                    <p v-html="siteData.fields['Web Description']"></p>
                </BlocksDetailDescription>
            </section>

            <div class="site-instructions">
                <section class="" v-if="!isMobile && siteData?.fields['Special entrance Instructions']">
                    <h2 class="title container">Entrance Instructions</h2>
                    <BlocksDetailDescription>
                        <p v-html="siteData.fields['Special entrance Instructions']"></p>
                    </BlocksDetailDescription>
                </section>

                <section class="" v-if="!isMobile && hasEntranceRestrictions">
                    <h2 class="title container">Entrance Restrictions</h2>
                    <BlocksDetailDescription>
                        <p v-for="restriction in entranceRestrictions" :key="restriction">{{ restriction }}</p>
                        <p v-if="siteData?.fields['Entrance Restriction Explanation']" v-html="siteData.fields['Entrance Restriction Explanation']"></p>
                    </BlocksDetailDescription>
                </section>

                <section class="" v-if="siteData?.fields['Visitor Experience']">
                    <h2 class="title container">Visitor Experience</h2>
                    <BlocksDetailDescription>
                        <p v-html="siteData.fields['Visitor Experience']"></p>
                    </BlocksDetailDescription>
                </section>
            </div>
            <GlobalSocialMedia
                v-if="siteData.fields"
                styleClass=""
                :facebook="siteData.fields['Facebook']"
                :twitter="siteData.fields['Twitter']"
                :instagram="siteData.fields['Instagram']"
                :website="siteData.fields['Website']" />

            <BlocksLiveEvents v-if="siteEvents && siteEvents.length > 0" :showViewAll="false" :items="siteEvents" title="Events at This Site" titleClass="subhead-2" />
            <BlocksToursList v-if="ohcTours && ohcTours.length" :items="ohcTours" :showViewAll="false" title="Featured on these OHC Itineraries" titleClass="subhead-2" />

            <div class="container neighborhood" v-if="siteNeighborhood">
                <p class="subhead-2">Explore the Neighborhood Area</p>
                <CardsNeighborhood :item="siteNeighborhood" />
            </div>

            </div>
        </article>
    </section>
</template>

<style lang="scss">
    .site-detail {
        position: relative;

        // No page-level sticky footer. The global footer block (CTA + footer) is
        // pinned to the bottom by the layout shell instead, so reserving a
        // viewport's height here as well just opened a gap between the content
        // and the CTA. See app/composables/useFooterCta.ts.

        .header-details {
            margin-top: 18px;
        }

        .detail-site-label {
            margin-bottom: 12px;
        }

        .site-type {
            padding: 6px 10px 4px;
            font-size: 12px;
            font-weight: 800;
            line-height: 1em;
            letter-spacing: 0.96px;
            text-transform: uppercase;
            background-color: $turquoise-light;
            display: inline-block;
            margin-top: 12px;
        }

        .actions {
            margin-top: 32px;
        }

        .description {
            margin: 36px 0;
        }

        .gray-tag {
            margin: 0 0 16px;
        }

        .address {
            margin-top: 4px;
        }

        .flex-row {
            display: flex;
            align-items: center;
        }

        .small-heavy {
            text-transform: uppercase;
        }

        .body-book.heavy,
        .small-book.heavy {
            font-weight: 800;
        }

        .building-details {
            margin-top: 32px;

            .details-title {
                text-transform: uppercase;
                font-size: 14px;
                font-weight: 800;
                line-height: 21px;
                letter-spacing: 1.12px;
                margin-bottom: 8px;
            }
        }

        .global-social-media {
            margin-bottom: 32px;

            @media (min-width: $tablet) {
                margin-bottom: 64px;
            }
        }

        .mobile-instructions {
            margin: 32px 0 0 0;

            .detail-toggle-container {
                margin-bottom: 0;
            }

            h2 {
                margin-bottom: 8px;
                text-transform: uppercase;
            }

            .title {
                font-size: 14px;
                font-weight: 800;
                line-height: 21px;
                letter-spacing: 1.12px;
                margin-bottom: 8px;
            }
        }

        // `.hd-container` is a flex column for the sticky footer, and flex
        // containers do NOT collapse adjacent margins — they add. So whatever
        // precedes the neighborhood block contributed its own bottom margin on
        // top of this one: social media's 64px + 60px here measured 124px.
        // Zeroing the predecessor makes the margin below the only contributor,
        // whichever block happens to sit there (social media, live events or the
        // tours list, all conditional). `:has()` is already used elsewhere in
        // this file, so it's an established dependency here.
        > .hd-container > *:has(+ .neighborhood) {
            margin-bottom: 0;
        }

        .neighborhood {
            // 40px to match the gap between the details paragraph and the
            // "VISITOR EXPERIENCE:" heading, measured on this page.
            margin-top: 40px;

            // Matches the 60px above it. The "Connect with CAC" button that used
            // to sit between this block and the tours CTA carried the spacing on
            // its own margins, so removing the button (Asana 1217333501985923)
            // dropped the neighborhood card straight onto the red band
            // (Asana 1217518674898018).
            margin-bottom: 60px;

            .subhead-2 {
                margin-bottom: 20px;
            }
        }

        .map-toggle {
            position: absolute;
            top: 16px;
            z-index: 57;
            right: 15px;
        }

        .image-wrapper {
            position: relative;

            // imagery-hero.vue gives .height-container `margin-top: 60px` at
            // $tablet to clear the floating map/photo toggle. That margin used to
            // COLLAPSE up and out of this wrapper, so it never rendered — but
            // .site-detail is now a flex column for the sticky footer, and flex
            // containers don't collapse margins, so it began pushing the carousel
            // 60px down: exactly the gap just removed from the map, reintroduced
            // in photo mode.
            //
            // Zeroed here rather than in imagery-hero, which is shared by tours,
            // events, stops and the archive — none of which are flex parents, so
            // their spacing is unchanged. Three classes (0,3,0) outrank the
            // component's scoped `.height-container[data-v-*]` (0,2,0), so this
            // needs no :deep().
            .height-container {
                margin-top: 0;
            }
        }

        .outer-wrapper.map {
            position: relative;

            // No margin-top. This carried `margin-top: 60px` at $tablet and up,
            // which dropped the map 60px below the nav and left a white band with
            // the map/photo toggle floating in it. The map is the first thing on
            // the page in map view and should sit flush, exactly as the hero image
            // does in photo view.
            //
            // The toggle is position: absolute, so it now overlays the map's top
            // right — which is what it ALREADY does over the hero image in photo
            // view, so the two modes finally agree. It doesn't collide with the
            // zoom controls, which sit bottom right on this map.
        }

        .site-instructions {
            section {
                padding-top: 10px;
            }

            .title {
                text-transform: uppercase;
                font-size: 14px;
                font-weight: 800;
                line-height: 21px;
                letter-spacing: 1.12px;
                margin-bottom: 8px;
            }
        }

        @media (min-width: $tablet) {
            max-width: $hd;
            width: 100vw;
            margin: auto;

            // `min-height: 60vh` lived here and, being later in the source, beat
            // the sticky-footer min-height at the top of this block from $tablet
            // up — the CTA still floated 122px clear of the bottom nav. The base
            // rule's calc is a strict improvement (full remaining viewport rather
            // than an arbitrary 60%) and is already breakpoint-correct via
            // --sticky-bar-height, so this no longer needs its own value.

            .actions {
                padding-right: 10px;
            }

            .outer-wrapper:has(.map-and-carousel-container) {
                height: 500px;
            }

            .map-and-carousel-container .map-square.short {
                height: 500px;
            }

            .map-toggle.top-actions {
                right: 9px;
                top: 8px;
                
                &.active {
                    right: 9px;
                    top: 8px;
                }

                .is-iframe & {    
                    top: 8px;
                    right: 16px;                    
                    
                    &.active {
                        left: 16px;
                        right: auto;
                    }
                }
            }

            .outer-container {
                display: inline-block;
            }

            .container.hours {
                display: inline-block;
                width: 50%;
                padding: 0 25px;
            }

            .block-amenities {
                display: inline-block;
                margin-top: 0px;
                margin-right: 18px;
            }

            .site-meta-container {
                display: inline-block;
                // width: 66.66%;

                .building-meta {
                    max-width: none;
                    display: flex;
                    gap: 50px;
                    margin-top: 0px;

                    .heavy {
                        margin-bottom: 16px;
                        line-height: 18px;
                        font-size: 16px;
                    }
                }
            }

            .amenities-site-meta-wrapper {
                display: flex;
                margin-top: 60px;
            }
        }

        @media (min-width: $desktop) {
            .site-meta-container .building-meta {
                gap: 80px;
            }

            .block-amenities {
                margin-right: 48px;
            }

            .actions {
                padding-right: 80px;
            }
        }
    }
</style>
